/**
 * Shared Sentry scrub helpers used by react-toolkit(.client).modifiers.jsx.
 * beforeSend / beforeSendTransaction stay inline in those files for ErrorEvent typing.
 */

/** @type {string[]} */
const SENSITIVE_QUERY_PARAMS = ['latmy', 'lonmy', 't', 'token']

/** @type {string[]} */
const SENTRY_FILTER_REQUEST_HEADERS = ['x-user', 'x-auth', 'authorization', 'cookie', 'forwarded-for', '-ip', 'remote-']

/** @type {string[]} */
const SENTRY_FILTER_RESPONSE_HEADERS = ['set-cookie', 'authorization']

/** @type {string[]} */
const SENTRY_FILTER_COOKIES = ['USER']

/**
 * @param {Record<string, string> | undefined} map
 * @param {string[]} terms
 */
const filterKeyValueMap = (map, terms) => {
	if (!map) return
	Object.keys(map).forEach((key) => {
		const keyLower = key.toLowerCase()
		const shouldFilter = terms.some((term) => keyLower.includes(term.toLowerCase()))
		if (shouldFilter) {
			map[key] = '[Filtered]'
		}
	})
}

/**
 * @param {string | undefined | null} qs
 * @returns {string | undefined | null}
 */
const scrubQueryString = (qs) => {
	if (!qs) return qs

	const raw = typeof qs === 'string' && qs.startsWith('?') ? qs.slice(1) : String(qs)
	const params = new URLSearchParams(raw)

	;[...params.keys()].forEach((key) => {
		if (SENSITIVE_QUERY_PARAMS.includes(key.toLowerCase())) {
			params.set(key, '[Filtered]')
		}
	})

	return params.toString()
}

/**
 * @param {string | undefined | null} urlString
 * @returns {string | undefined | null}
 */
const scrubUrlQueryParams = (urlString) => {
	if (!urlString || typeof urlString !== 'string') {
		return urlString
	}

	try {
		const url = new URL(urlString)
		url.searchParams.forEach((_value, key) => {
			if (SENSITIVE_QUERY_PARAMS.includes(key.toLowerCase())) {
				url.searchParams.set(key, '[Filtered]')
			}
		})
		return url.toString()
	} catch {
		const qIndex = urlString.indexOf('?')
		if (qIndex === -1) {
			return urlString
		}
		return `${urlString.slice(0, qIndex + 1)}${scrubQueryString(urlString.slice(qIndex + 1))}`
	}
}

/**
 * Axios abort / cancel errors are expected (e.g. racing fetches) and should not create Sentry issues.
 * @param {{ exception?: { values?: Array<{ type?: string, value?: string }> } }} event
 * @returns {boolean}
 */
const isCanceledSentryEvent = (event) => {
	const values = event.exception?.values
	if (!values?.length) {
		return false
	}

	return values.some((exception) => exception.type === 'CanceledError' || exception.value === 'canceled')
}

module.exports = {
	SENSITIVE_QUERY_PARAMS,
	SENTRY_FILTER_REQUEST_HEADERS,
	SENTRY_FILTER_RESPONSE_HEADERS,
	SENTRY_FILTER_COOKIES,
	filterKeyValueMap,
	scrubQueryString,
	scrubUrlQueryParams,
	isCanceledSentryEvent
}
